import { useEffect, useRef, useState } from "react";
import { fetchClientConfig } from "../api/systemStatus";
import "./ListPagination.css";

const DEFAULT_PAGE_SIZE = 100;

let cachedPageSize: number | null = null;
let pageSizePromise: Promise<number> | null = null;

/**
 * Sunucu taraflı sayfalamada sayfa boyutu. runtime_settings'ten (list_page_size)
 * bir kez okunur; okunana kadar varsayilan 100 kullanilir.
 */
export function useListPageSize(): number {
  const [size, setSize] = useState<number>(cachedPageSize ?? DEFAULT_PAGE_SIZE);

  useEffect(() => {
    if (cachedPageSize !== null) return undefined;
    if (!pageSizePromise) {
      pageSizePromise = fetchClientConfig()
        .then((cfg) => {
          const value = Number(cfg.list_page_size);
          cachedPageSize = Number.isFinite(value) && value >= 10 && value <= 1000 ? value : DEFAULT_PAGE_SIZE;
          return cachedPageSize;
        })
        .catch(() => {
          cachedPageSize = DEFAULT_PAGE_SIZE;
          return DEFAULT_PAGE_SIZE;
        });
    }
    let mounted = true;
    void pageSizePromise.then((value) => {
      if (mounted) setSize(value);
    });
    return () => {
      mounted = false;
    };
  }, []);

  return size;
}

/**
 * Offset state'i + filtre degisiminde ilk sayfaya donus.
 * filterKey: offset disindaki tum filtrelerin serilestirilmis hali.
 */
export function usePagedOffset(filterKey: string): { offset: number; setOffset: (o: number) => void } {
  const [offset, setOffset] = useState(0);
  const prevKey = useRef(filterKey);

  useEffect(() => {
    if (prevKey.current !== filterKey) {
      prevKey.current = filterKey;
      setOffset(0);
    }
  }, [filterKey]);

  return { offset, setOffset };
}

type ListPaginationBarProps = {
  total: number;
  offset: number;
  limit: number;
  loading?: boolean;
  onOffsetChange: (offset: number) => void;
};

export function ListPaginationBar({ total, offset, limit, loading, onOffsetChange }: ListPaginationBarProps) {
  if (total <= limit && offset === 0) return null;

  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(total, offset + limit);

  return (
    <div className="kp-pagination" role="navigation" aria-label="Sayfalama">
      <button
        type="button"
        className="kp-btn kp-btn--ghost kp-pagination__btn"
        disabled={loading || offset === 0}
        onClick={() => onOffsetChange(Math.max(0, offset - limit))}
      >
        ‹ Önceki
      </button>
      <span className="kp-pagination__info">
        {from}–{to} / {total} · Sayfa {currentPage}/{totalPages}
      </span>
      <button
        type="button"
        className="kp-btn kp-btn--ghost kp-pagination__btn"
        disabled={loading || offset + limit >= total}
        onClick={() => onOffsetChange(offset + limit)}
      >
        Sonraki ›
      </button>
    </div>
  );
}
